import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ImportantSvg from '@/components/common/ImportantSvg';
import PdpEasyPayPopup from '@/components/pdp_easypay_popup/PdpEasyPayPopup';
import http from '@/tscapi/service/httpservice';
import { Cookie } from '@/tscapi/utils/cookie';

import { useAuthenticated } from './hooks/useAuthenticated';
import { useCart } from './hooks/useCart';
import { useEasyPay } from './hooks/useEasyPay';
import { en, fr } from './i18n';

export interface EasyPayData {
    title: string;
    content: string;
}
interface EasyPayProps {
    setIsPaypalDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    showEasyPayNotice: boolean;
    setEasyWarningBlockCheckout: React.Dispatch<React.SetStateAction<boolean>>;
    setShowEasyPayNotice: React.Dispatch<React.SetStateAction<boolean>>;
}
export const Easypay = ({
    setIsPaypalDisabled,
    showEasyPayNotice: easyPayWarningNotice,
    setShowEasyPayNotice: setEasyPayWarningNotice,
    setEasyWarningBlockCheckout,
}: EasyPayProps) => {
    const cookie: Cookie = new Cookie();
    const [showEasyPaySelectbox, setShowEasyPaySelectbox] = useState(false);
    const [showEasyPayNotice, setShowEasyPayNotice] = useState(false);
    const [showEasyPayPopup, setShowEasyPayPopup] = useState(false);
    const [selectedOption, setSelectedOption] = useState('none');
    const [easyPayPopupData, setEasyPayPopupData] = useState<EasyPayData>({ title: '', content: '' });
    const [isEasyPayAvailable, setIsEasyPayAvailable] = useState(false);
    const [promotionalEasyPayApplied, setPromotionalEasyPayApplied] = useState(false);
    //translation
    const { i18n, t } = useTranslation('shoppingbag');
    i18n.addResourceBundle('en', 'shoppingbag', en, true, true);
    i18n.addResourceBundle('fr', 'shoppingbag', fr, true, true);
    //react-query data hooks
    const isAuthenticated = useAuthenticated();
    const { data } = useCart(isAuthenticated);
    const easyPayMutation = useEasyPay(cookie.CartGuid);

    useEffect(() => {
        if (data?.OrderSummary?.EasyPay != undefined && data?.OrderSummary?.EasyPay.NoOfInstallments > 1) {
            setShowEasyPaySelectbox(true);
        }
        if (data?.AvailableInstallmentsBreakdown?.length > 1) {
            setIsEasyPayAvailable(true);
        } else {
            setIsEasyPayAvailable(false);
        }
        const easyPayNoOfInstallments = data?.OrderSummary?.EasyPay?.NoOfInstallments;
       
        if (
            data?.PromotionalEasyPayInstallment
            && easyPayNoOfInstallments == data?.PromotionalEasyPayInstallment?.NoOfInstallments
            && data?.CreditCard?.IsIssuerRogers
        ) {
            setPromotionalEasyPayApplied(true);
        } else {
            setPromotionalEasyPayApplied(false);
        }
        setSelectedOption(data?.OrderSummary?.EasyPay ? data?.OrderSummary?.EasyPay?.NoOfInstallments.toString() : '1');
    }, [data]);

    useEffect(() => {
        if (selectedOption == '1' && showEasyPaySelectbox) {
            setEasyWarningBlockCheckout(true);
        } else {
            setEasyWarningBlockCheckout(false);
        }
    }, [selectedOption, setEasyWarningBlockCheckout, showEasyPaySelectbox]);

    const fetchEasyPayArticle = async () => {
        async function fetchArticle() {
            const headers = {
                'Content-Type': 'application/json',
                'cache-control': 'no-cache',
                currentdatetime: new Cookie().CurrentDate,
            };
            const base = '/api/v2/';
            const lang = new Cookie().Language.substring(0, 2) || 'en';
            const easyPayUrl = base + lang + '/customerservice/easy-pay';
            try {
                const data = await http.get(easyPayUrl, {
                    headers: headers,
                    transformRequest: (data, headers) => {
                        delete headers.Authorization;
                        return data;
                    },
                });
                return data.data.Article;
            } catch (e) {
                return null;
            }
        }
        //if content is null, fetch easy pay data
        if (easyPayPopupData.content == '') {
            const article = await fetchArticle();
            if (article) {
                setEasyPayPopupData({ title: article.Title, content: article.Content });
                setShowEasyPayPopup(true);
            }
        } else {
            setShowEasyPayPopup(true);
        }
    };

    return (
        <>
            {isEasyPayAvailable && (
                <>
                    {' '}
                    <div className="mb-5 flex items-center">
                        <input
                            type="checkbox"
                            key={1}
                            id="easyPayCheckbox"
                            className="visually-hidden"
                            checked={showEasyPaySelectbox}
                            onChange={(e) => {
                                if (
                                    !e.target.checked &&
                                    data?.OrderSummary?.EasyPay != null &&
                                    data?.OrderSummary?.EasyPay.NoOfInstallments > 1
                                ) {
                                    easyPayMutation.mutate({
                                        noOfInstallments: 1,
                                        onSuccess: () => {
                                            setIsPaypalDisabled(false);
                                        },
                                    });
                                }
                                setEasyPayWarningNotice(false);
                                setShowEasyPaySelectbox(e.target.checked);
                                if (e.target.checked) {
                                    setIsPaypalDisabled(true);
                                } else {
                                    setIsPaypalDisabled(false);
                                }
                            }}
                        />
                        <label className="summary__easypay--checkbox-label" htmlFor="easyPayCheckbox">
                            {t('Select Monthly Payments with')}
                        </label>
                        <button
                            className="ml-[5px] inline bg-none text-sm font-semibold uppercase text-zinc-900 underline"
                            onClick={() => fetchEasyPayArticle()}
                        >
                            {t('Easy Pay®')}
                        </button>
                    </div>
                    {showEasyPaySelectbox && (
                        <>
                            {/* Only show installment select box choices if not promotionaleasypay  !promotionalEasyPayApplied */}
                            {data?.AvailableInstallments && (
                                <>
                                    <select
                                        className="-mt-1 mb-5 ml-0 w-full appearance-none rounded-none border-x-0 border-b border-t-0 border-solid border-b-black bg-transparent bg-[length:0.625rem] bg-no-repeat py-[0.3125rem] pl-[5px] pr-[1.5625rem] text-base font-light [background-position:95%]"
                                        value={selectedOption}
                                        style={{ backgroundImage: 'url(/App_Assets/public/selectbox-chevron.svg)' }}
                                        onChange={(e) => {
                                            setSelectedOption(e.target.value);
                                            setEasyPayWarningNotice(false);
                                            easyPayMutation.mutate({
                                                noOfInstallments: parseInt(e.target.value),
                                                onSuccess: () => {},
                                            });
                                        }}
                                    >
                                        <option value="1">
                                            {t('Number of Installments')} ({t('select')})
                                        </option>
                                        {data?.AvailableInstallmentsBreakdown.slice(1).map((payment, index) => (
                                            <option key={payment.NoOfInstallments} value={payment.NoOfInstallments}>
                                                {payment.MonthlyPayment}
                                            </option>
                                        ))}

                                        {data?.PromotionalEasyPayInstallment && data?.CreditCard?.IsIssuerRogers && (
                                            <option
                                                key="promoEasypay"
                                                value={data?.PromotionalEasyPayInstallment?.NoOfInstallments}
                                            >
                                                {data?.PromotionalEasyPayInstallment?.MonthlyPayment}
                                            </option>
                                        )}
                                    </select>
                                </>
                            )}

                            {data?.OrderSummary?.EasyPay?.NoOfInstallments > 1 && (
                                <>
                                    <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                        <div className="flex-[1_1_73%] text-left">
                                            <span className="font-semibold">{t('Your total payment today')}</span>
                                        </div>
                                        <div className="flex-[1_1_27%] text-right">
                                            <span className="font-semibold">
                                                {data?.OrderSummary?.EasyPay?.TotalPaymentToday}*
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                        <div className="flex-[1_1_73%] text-left">
                                            <span>{t('Payment amount left after today')}</span>
                                        </div>
                                        <div className="flex-[1_1_27%] text-right">
                                            <span>{data?.OrderSummary?.EasyPay?.BalanceRemainingAfterToday}</span>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap p-0 pb-2.5 text-base font-light">
                                        <div className="flex-[1_1_73%] text-left">
                                            <span>{data?.OrderSummary?.EasyPay?.NoOfFutureMonthlyPayments}</span>{' '}
                                            <span>{t('future monthly payments')}</span>
                                        </div>
                                        <div className="flex-[1_1_27%] text-right">
                                            <span>{data?.OrderSummary?.EasyPay?.FutureMonthlyPayment}</span>
                                        </div>
                                    </div>
                                    <div className="-mt-2 flex flex-wrap p-0 pb-2.5 text-base font-light">
                                        <div className="flex-[1_1_73%] text-left">
                                            <span className="text-[10px] font-normal">
                                                *{t('Shipping and taxes have been added to your first payment.')}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                    {(showEasyPayNotice || easyPayWarningNotice) && (
                        <div className="relative mb-5 border-2 border-solid border-[#ea0c0c] py-2.5 pl-10 pr-6">
                            <ImportantSvg className="absolute left-2 top-2" />
                            <b className="text-[16px] font-bold">{t('Please select a number of installments')}</b>{' '}
                            <span>{t('or unselect the Monthly Payments option to Proceed to checkout')}</span>
                        </div>
                    )}
                    {showEasyPayPopup && (
                        <PdpEasyPayPopup
                            title={easyPayPopupData.title}
                            content={easyPayPopupData.content}
                            onCloseClick={() => {
                                setShowEasyPayPopup(false);
                            }}
                        />
                    )}
                </>
            )}
        </>
    );
};
